import PouchDB from "pouchdb";
import PouchDBUpsert from "pouchdb-upsert";
import { type OperatorType } from "@/types";
import { operatorsLogger } from "@/utils/loggers";
import { normalizeTimestamp } from "@/utils/dateAndTime";
import HamrsApiService from "@/services/HamrsApiService";
import OperatorJsonRepo from "@/repos/OperatorJsonRepo";
import { useOfflineOperatorInitStore } from "@/stores/OfflineOperatorInitStore";
import { useOnline } from '@vueuse/core';

// Add PouchDB plugins
PouchDB.plugin(PouchDBUpsert);

// Initialize online status at module level
const online = useOnline();

export type OperatorDbMetaData = {
  _id: string;
  lastUpdated: number;
  totalOperators: number;
  isInitialized: boolean;
};

// Create PouchDB instance
const operatorDb = new PouchDB("operators.db");
const METADATA_DOC_ID = "OPERATORS_METADATA";
const UPDATE_INTERVAL_HOURS = 24; // Check for updates daily

// Sync guard to prevent overlapping operations
let isSyncing = false;
let isInitializingFromStatic = false;
let isInitializing = false;

/**
 * Helper function to normalize an operator object for database storage
 * @param operator The operator object to normalize
 * @returns A new operator object with _id set to callsign
 */
function toDbOperator(operator: OperatorType): OperatorType & { _id: string } {
  return {
    ...operator,
    _id: operator.callsign.toUpperCase(),
    callsign: operator.callsign.toUpperCase(),
    updatedAt: operator.updatedAt ? normalizeTimestamp(operator.updatedAt) : Date.now()
  };
}

const OperatorPouchRepo = {
  async initializeOperatorsData(): Promise<void> {
    // Prevent overlapping initialization
    if (isInitializing) {
      operatorsLogger("Initialization already in progress, skipping duplicate request");
      return;
    }

    const initStore = useOfflineOperatorInitStore();
    isInitializing = true;

    try {
      const metadata = await this.getMetadata();

      // If not initialized, bootstrap from static data first (with UI)
      if (!metadata.isInitialized) {
        initStore.startInitialization();
        operatorsLogger("Bootstrapping from static data first");
        await this.initializeFromStaticData();
        // After static data is loaded, try to sync with API for updates (invisible)
        this.syncUpdatesInBackground();
        return;
      }

      // If already initialized, no UI needed - just check for background updates
      const shouldUpdate = this.shouldUpdateOperators(metadata);

      if (shouldUpdate && online.value) {
        operatorsLogger("Checking for operator data updates in background");
        // Background sync without UI
        await this.syncOperatorsFromServer(metadata, false);
      } else if (shouldUpdate && !online.value) {
        operatorsLogger("Updates available but device is offline");
      } else {
        operatorsLogger("Operators data is up to date");
      }
    } catch (error) {
      operatorsLogger("Error initializing operators data, falling back to static data", error);
      initStore.startInitialization();
      await this.initializeFromStaticData();
    } finally {
      isInitializing = false;
    }
  },

  // Background sync for updates after initial bootstrap (only on app start)
  syncUpdatesInBackground(): void {
    // Don't await this - let it run in background
    setTimeout(async () => {
      if (!online.value) {
        operatorsLogger("Skipping background sync - device is offline");
        return;
      }

      try {
        operatorsLogger("Starting one-time background sync for operator updates");
        const metadata = await this.getMetadata();
        // showProgress = false for background sync
        await this.syncOperatorsFromServer(metadata, false);
      } catch (error) {
        operatorsLogger("Background sync failed - user can manually refresh later", error);
      }
    }, 5000); // Wait 5 seconds before starting background sync
  },

  async getMetadata(): Promise<OperatorDbMetaData> {
    try {
      return await operatorDb.get<OperatorDbMetaData>(METADATA_DOC_ID);
    } catch (error: any) {
      if (error.status === 404) {
        // No metadata exists, return default
        return {
          _id: METADATA_DOC_ID,
          lastUpdated: 0,
          totalOperators: 0,
          isInitialized: false
        };
      }
      throw error;
    }
  },

  async updateMetadata(updates: Partial<OperatorDbMetaData>): Promise<void> {
    try {
      await operatorDb.upsert(METADATA_DOC_ID, (doc: any) => ({
        _id: METADATA_DOC_ID,
        lastUpdated: 0,
        totalOperators: 0,
        isInitialized: false,
        ...doc,
        ...updates
      }));
    } catch (error) {
      operatorsLogger("Error updating metadata", error);
      throw error;
    }
  },

  shouldUpdateOperators(metadata: OperatorDbMetaData): boolean {
    if (!metadata.isInitialized) {
      return true;
    }

    const hoursSinceUpdate = (Date.now() - metadata.lastUpdated) / (1000 * 60 * 60);

    return hoursSinceUpdate >= UPDATE_INTERVAL_HOURS;
  },

  async syncOperatorsFromServer(metadata: OperatorDbMetaData, showProgress: boolean = true): Promise<void> {
    // Prevent overlapping syncs
    if (isSyncing) {
      operatorsLogger("Sync already in progress, skipping duplicate request");
      return;
    }

    const initStore = useOfflineOperatorInitStore();
    isSyncing = true;

    try {
      // Use appropriate timestamp based on initialization status
      const rawStaticTimestamp = OperatorJsonRepo.lastUpdatedAt();
      if (!rawStaticTimestamp) {
        operatorsLogger('Static data timestamp invalid, defaulting to metadata.lastUpdated');
      }

      // Normalize static data timestamp to ensure consistent millisecond precision
      const staticDataTimestamp = rawStaticTimestamp ? normalizeTimestamp(rawStaticTimestamp) : 0;
      const sinceTimestamp = metadata.isInitialized
        ? metadata.lastUpdated  // Use last successful sync timestamp for subsequent updates
        : (staticDataTimestamp || metadata.lastUpdated);  // Use static data timestamp only for initial sync

      // Debug logging
      operatorsLogger(`Debug - metadata.lastUpdated: ${new Date(metadata.lastUpdated).toISOString()}`);
      operatorsLogger(`Debug - staticDataTimestamp: ${new Date(staticDataTimestamp).toISOString()}`);
      operatorsLogger(`Debug - using sinceTimestamp: ${new Date(sinceTimestamp).toISOString()}`);

      if (showProgress) {
        initStore.setState('downloading');
      }
      operatorsLogger(`Fetching operators updated since: ${new Date(sinceTimestamp).toISOString()}`);

      const operators = await HamrsApiService.fetchOperators(sinceTimestamp);

      if (operators.length > 0) {
        operatorsLogger(`Syncing ${operators.length} operators to local database`);
        if (showProgress) {
          initStore.setState('processing');
          initStore.setProgress(0, operators.length);
        }

        // Batch upsert all operators with progress reporting
        const batchSize = 50; // Smaller batch size for better progress updates
        let processedCount = 0;

        for (let i = 0; i < operators.length; i += batchSize) {
          const batch = operators.slice(i, i + batchSize);

          // Process batch
          await Promise.all(
            batch.map(operator =>
              operatorDb.upsert(operator.callsign.toUpperCase(), () => toDbOperator(operator))
            )
          );

          processedCount += batch.length;
          if (showProgress) {
            initStore.setProgress(processedCount, operators.length);
          }

          // Yield control to prevent blocking the UI
          await new Promise(resolve => setTimeout(resolve, 0));
        }

        // Update metadata - use db.info() for efficient count
        let totalOperators = 0;
        try {
          const dbInfo = await operatorDb.info();
          // PouchDB info() returns doc_count - subtract 1 for metadata document
          totalOperators = Math.max(0, (dbInfo.doc_count || 0) - 1);
        } catch (error) {
          operatorsLogger('Failed to get database info, using fallback count of 0', error);
          totalOperators = 0;
        }

        // Advance to the max operator updatedAt we actually applied
        const maxUpdatedAt = operators.reduce(
          (acc, op) => {
            try {
              return Math.max(acc, normalizeTimestamp(op.updatedAt ?? 0));
            } catch (error) {
              // Skip invalid timestamps
              return acc;
            }
          },
          metadata.lastUpdated
        );
        await this.updateMetadata({
          lastUpdated: maxUpdatedAt,
          totalOperators,
          isInitialized: true
        });

        operatorsLogger(`Successfully synced ${operators.length} operators`);
        if (showProgress) {
          initStore.setCompleted();
        }
      } else {
        operatorsLogger("No new operators to sync");
        // Do not advance beyond what we queried
        await this.updateMetadata({ lastUpdated: Math.max(metadata.lastUpdated, sinceTimestamp) });
        if (showProgress) {
          initStore.setCompleted();
        }
      }
    } catch (error) {
      operatorsLogger("Error syncing operators from server", error);

      // Only show error in UI if this is a foreground sync
      if (showProgress) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        initStore.setError(errorMessage);
      }

      throw error;
    } finally {
      isSyncing = false;
    }
  },

  async initializeFromStaticData(): Promise<void> {
    // Prevent overlapping static data initialization
    if (isInitializingFromStatic) {
      operatorsLogger("Static data initialization already in progress, skipping duplicate request");
      return;
    }

    const initStore = useOfflineOperatorInitStore();
    isInitializingFromStatic = true;

    try {
      if (!window.operatorsData) {
        throw new Error("Static operators data not available");
      }

      const metadata = await this.getMetadata();
      if (metadata.isInitialized) {
        operatorsLogger("Database already initialized, skipping static data import");
        initStore.setCompleted();
        return;
      }

      operatorsLogger("Initializing from static operators data");
      const operatorsDataWrapper = window.operatorsData!;
      const operators = operatorsDataWrapper.operators;

      initStore.setState('processing');
      initStore.setProgress(0, operators.length);

      // Batch import static data with progress reporting
      const batchSize = 50;
      let processedCount = 0;

      for (let i = 0; i < operators.length; i += batchSize) {
        const batch = operators.slice(i, i + batchSize);

        await Promise.all(
          batch.map(operator =>
            operatorDb.upsert(operator.callsign.toUpperCase(), () => toDbOperator(operator))
          )
        );

        processedCount += batch.length;
        initStore.setProgress(processedCount, operators.length);

        // Yield control to prevent blocking the UI
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      // When initializing from static data, use its timestamp as our starting point
      const staticDataTimestamp = normalizeTimestamp(OperatorJsonRepo.lastUpdatedAt());
      await this.updateMetadata({
        lastUpdated: staticDataTimestamp,
        totalOperators: operators.length,
        isInitialized: true
      });

      operatorsLogger(`Initialized database with ${operators.length} operators from static data`);
      initStore.setCompleted();
    } catch (error) {
      operatorsLogger("Error initializing from static data", error);
      initStore.setError(error instanceof Error ? error.message : 'Failed to initialize from static data');
      throw error;
    } finally {
      isInitializingFromStatic = false;
    }
  },

  async getCount(): Promise<number> {
    try {
      const result = await operatorDb.allDocs({
        include_docs: false
      });

      // Filter out the metadata document from the count
      const operatorCount = result.rows.filter(row => row.id !== METADATA_DOC_ID).length;
      return operatorCount;
    } catch (error) {
      console.error("Error getting count", error);
      throw error;
    }
  },

  async findByCallsign(callsign: string): Promise<OperatorType | null> {
    try {
      const operator = await operatorDb.get<OperatorType>(callsign.toUpperCase());
      return operator;
    } catch (error: any) {
      if (error.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async search(query: string, limit: number = 50): Promise<OperatorType[]> {
    try {
      const upperQuery = query.toUpperCase();

      // Since callsign is the _id, we can use allDocs with startkey/endkey for prefix search
      const result = await operatorDb.allDocs<OperatorType>({
        include_docs: true,
        startkey: upperQuery,
        endkey: upperQuery + '\ufff0',
        limit
      });

      // Filter out metadata doc and map to operator types
      return result.rows
        .filter(row => row.id !== METADATA_DOC_ID && row.doc)
        .map(row => row.doc as OperatorType);
    } catch (error) {
      operatorsLogger("Error searching operators", error);
      throw error;
    }
  },

  async create(operator: OperatorType): Promise<OperatorType> {
    try {
      const normalizedOperator = toDbOperator(operator);
      await operatorDb.upsert(operator.callsign.toUpperCase(), () => normalizedOperator);
      operatorsLogger("Operator Created", operator.callsign);
      return normalizedOperator;
    } catch (error) {
      console.error("Error creating operator", error);
      throw error;
    }
  },

  async checkForUpdates(): Promise<void> {
    if (!online.value) {
      operatorsLogger("Cannot check for updates - device is offline");
      throw new Error("Device is offline");
    }

    operatorsLogger("Checking for operator updates...");
    const metadata = await this.getMetadata();
    await this.syncOperatorsFromServer(metadata); // Use existing timestamp
  },

  async forceSync(): Promise<void> {
    if (!online.value) {
      operatorsLogger("Cannot force sync - device is offline");
      throw new Error("Device is offline");
    }

    operatorsLogger("Force syncing operators using earliest available timestamp...");
    const metadata = await this.getMetadata();
    // Use the earliest timestamp we have available to avoid missing any updates
    const staticDataTimestamp = normalizeTimestamp(OperatorJsonRepo.lastUpdatedAt());
    const earliestTimestamp = metadata.isInitialized ?
      Math.min(metadata.lastUpdated, staticDataTimestamp) :
      staticDataTimestamp;
    await this.syncOperatorsFromServer({ ...metadata, lastUpdated: earliestTimestamp });
  }
};


export default OperatorPouchRepo;